import { useState, useEffect } from 'react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { useToast } from '@/hooks/use-toast'
import { useSendEmail } from '@/hooks/use-api'
import { ChevronDown, ChevronUp, Send } from 'lucide-react'
import { GRACKLE_COLORS } from '@/lib/theme'
import type { Email } from '@/types'
import { explainError } from '@/lib/explain-error'

interface ComposeEmailModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  replyTo?: Email | null
  relatedToId?: string
  defaultTo?: string
}

export function ComposeEmailModal({
  open,
  onOpenChange,
  replyTo,
  relatedToId,
  defaultTo,
}: ComposeEmailModalProps) {
  const { toast } = useToast()
  const sendMutation = useSendEmail()

  const [toAddress, setToAddress] = useState('')
  const [ccAddress, setCcAddress] = useState('')
  const [bccAddress, setBccAddress] = useState('')
  const [subject, setSubject] = useState('')
  const [textBody, setTextBody] = useState('')
  const [showCcBcc, setShowCcBcc] = useState(false)
  const [errors, setErrors] = useState<Record<string, string>>({})

  // Pre-fill fields for replies or default recipient
  useEffect(() => {
    if (open && replyTo) {
      setToAddress(replyTo.from_address || '')
      const replySubject = replyTo.subject || ''
      setSubject(replySubject.startsWith('Re:') ? replySubject : `Re: ${replySubject}`)
      setTextBody('')
    } else if (open && defaultTo) {
      resetForm()
      setToAddress(defaultTo)
    } else if (open) {
      resetForm()
    }
  }, [open, replyTo, defaultTo])

  const resetForm = () => {
    setToAddress('')
    setCcAddress('')
    setBccAddress('')
    setSubject('')
    setTextBody('')
    setShowCcBcc(false)
    setErrors({})
  }

  const BETA_DOMAIN = '@grackledocs.com'
  const BETA_MSG = `Beta: Only ${BETA_DOMAIN} addresses are currently supported`

  const validateEmail = (email: string): boolean => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())
  }

  const isAllowedDomain = (email: string): boolean => {
    return email.trim().toLowerCase().endsWith(BETA_DOMAIN)
  }

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {}

    if (!toAddress.trim()) {
      newErrors.to = 'Recipient email address is required'
    } else {
      const addresses = toAddress
        .split(',')
        .map((a) => a.trim())
        .filter(Boolean)
      const invalid = addresses.filter((a) => !validateEmail(a))
      if (invalid.length > 0) {
        newErrors.to = `Invalid email address: ${invalid[0]}`
      } else {
        const restricted = addresses.filter((a) => !isAllowedDomain(a))
        if (restricted.length > 0) {
          newErrors.to = BETA_MSG
        }
      }
    }

    if (ccAddress.trim()) {
      const addresses = ccAddress
        .split(',')
        .map((a) => a.trim())
        .filter(Boolean)
      const invalid = addresses.filter((a) => !validateEmail(a))
      if (invalid.length > 0) {
        newErrors.cc = `Invalid email address: ${invalid[0]}`
      } else {
        const restricted = addresses.filter((a) => !isAllowedDomain(a))
        if (restricted.length > 0) {
          newErrors.cc = BETA_MSG
        }
      }
    }

    if (bccAddress.trim()) {
      const addresses = bccAddress
        .split(',')
        .map((a) => a.trim())
        .filter(Boolean)
      const invalid = addresses.filter((a) => !validateEmail(a))
      if (invalid.length > 0) {
        newErrors.bcc = `Invalid email address: ${invalid[0]}`
      } else {
        const restricted = addresses.filter((a) => !isAllowedDomain(a))
        if (restricted.length > 0) {
          newErrors.bcc = BETA_MSG
        }
      }
    }

    if (!subject.trim()) {
      newErrors.subject = 'Subject is required'
    }

    if (!textBody.trim()) {
      newErrors.body = 'Message body is required'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!validate()) return

    try {
      const response = await sendMutation.mutateAsync({
        to_address: toAddress.trim(),
        cc_address: ccAddress.trim() || undefined,
        bcc_address: bccAddress.trim() || undefined,
        subject: subject.trim(),
        text_body: textBody.trim(),
        related_to_id: relatedToId || undefined,
        parent_id: replyTo?.id || undefined,
        thread_identifier: replyTo?.thread_identifier || undefined,
      } as Partial<Email>)

      // Check for SMTP warning (saved as draft)
      const warning = (response as any)?.warning
      if (warning) {
        toast({
          title: 'Email saved as draft',
          description: warning,
        })
      } else {
        toast({
          title: 'Email sent',
          description: `Email sent to ${toAddress}`,
        })
      }

      resetForm()
      onOpenChange(false)
    } catch (error) {
      const errorMessage = explainError(error, 'Failed to send email')
      toast({
        title: 'Error',
        description: errorMessage,
        variant: 'destructive',
      })
    }
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(isOpen) => {
        if (!isOpen) resetForm()
        onOpenChange(isOpen)
      }}
    >
      <DialogContent className="max-w-2xl" aria-describedby="compose-email-description">
        <DialogHeader>
          <DialogTitle>{replyTo ? 'Reply to Email' : 'Compose Email'}</DialogTitle>
          <DialogDescription id="compose-email-description">
            {replyTo
              ? `Replying to ${replyTo.from_name || replyTo.from_address || 'sender'}.`
              : 'Compose and send a new email.'}{' '}
            Fields marked with * are required.
          </DialogDescription>
        </DialogHeader>

        {/* Beta notice */}
        <div
          className="rounded-md p-3"
          style={{
            backgroundColor: GRACKLE_COLORS.paleBlue,
            borderWidth: '1px',
            borderStyle: 'solid',
            borderColor: GRACKLE_COLORS.navy,
          }}
          role="note"
        >
          <p className="text-sm" style={{ color: GRACKLE_COLORS.navy }}>
            Email sending is currently in <strong>Beta</strong> and restricted to{' '}
            <strong>@grackledocs.com</strong> addresses only.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* To */}
          <div>
            <Label htmlFor="email-to">To *</Label>
            <Input
              id="email-to"
              type="text"
              value={toAddress}
              onChange={(e) => {
                setToAddress(e.target.value)
                if (errors.to)
                  setErrors((prev) => {
                    const n = { ...prev }
                    delete n.to
                    return n
                  })
              }}
              placeholder="name@grackledocs.com"
              aria-required="true"
              aria-invalid={!!errors.to}
              aria-describedby={errors.to ? 'email-to-error' : 'email-to-hint'}
            />
            {errors.to ? (
              <p id="email-to-error" className="text-sm text-destructive mt-1" role="alert">
                {errors.to}
              </p>
            ) : (
              <p id="email-to-hint" className="text-xs text-muted-foreground mt-1">
                Separate multiple addresses with commas
              </p>
            )}
          </div>

          {/* CC/BCC toggle */}
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => setShowCcBcc(!showCcBcc)}
            className="text-xs text-muted-foreground px-0"
            aria-expanded={showCcBcc}
            aria-controls="cc-bcc-fields"
          >
            {showCcBcc ? (
              <ChevronUp className="mr-1 h-3 w-3" aria-hidden="true" />
            ) : (
              <ChevronDown className="mr-1 h-3 w-3" aria-hidden="true" />
            )}
            {showCcBcc ? 'Hide CC/BCC' : 'Show CC/BCC'}
          </Button>

          {/* CC / BCC */}
          {showCcBcc && (
            <div id="cc-bcc-fields" className="space-y-4">
              <div>
                <Label htmlFor="email-cc">CC</Label>
                <Input
                  id="email-cc"
                  type="text"
                  value={ccAddress}
                  onChange={(e) => {
                    setCcAddress(e.target.value)
                    if (errors.cc)
                      setErrors((prev) => {
                        const n = { ...prev }
                        delete n.cc
                        return n
                      })
                  }}
                  placeholder="cc@example.com"
                  aria-invalid={!!errors.cc}
                  aria-describedby={errors.cc ? 'email-cc-error' : undefined}
                />
                {errors.cc && (
                  <p id="email-cc-error" className="text-sm text-destructive mt-1" role="alert">
                    {errors.cc}
                  </p>
                )}
              </div>
              <div>
                <Label htmlFor="email-bcc">BCC</Label>
                <Input
                  id="email-bcc"
                  type="text"
                  value={bccAddress}
                  onChange={(e) => {
                    setBccAddress(e.target.value)
                    if (errors.bcc)
                      setErrors((prev) => {
                        const n = { ...prev }
                        delete n.bcc
                        return n
                      })
                  }}
                  placeholder="bcc@example.com"
                  aria-invalid={!!errors.bcc}
                  aria-describedby={errors.bcc ? 'email-bcc-error' : undefined}
                />
                {errors.bcc && (
                  <p id="email-bcc-error" className="text-sm text-destructive mt-1" role="alert">
                    {errors.bcc}
                  </p>
                )}
              </div>
            </div>
          )}

          {/* Subject */}
          <div>
            <Label htmlFor="email-subject">Subject *</Label>
            <Input
              id="email-subject"
              type="text"
              value={subject}
              onChange={(e) => {
                setSubject(e.target.value)
                if (errors.subject)
                  setErrors((prev) => {
                    const n = { ...prev }
                    delete n.subject
                    return n
                  })
              }}
              placeholder="Email subject"
              aria-required="true"
              aria-invalid={!!errors.subject}
              aria-describedby={errors.subject ? 'email-subject-error' : undefined}
            />
            {errors.subject && (
              <p id="email-subject-error" className="text-sm text-destructive mt-1" role="alert">
                {errors.subject}
              </p>
            )}
          </div>

          {/* Body */}
          <div>
            <Label htmlFor="email-body">Message *</Label>
            <Textarea
              id="email-body"
              value={textBody}
              onChange={(e) => {
                setTextBody(e.target.value)
                if (errors.body)
                  setErrors((prev) => {
                    const n = { ...prev }
                    delete n.body
                    return n
                  })
              }}
              placeholder="Type your message..."
              rows={10}
              aria-required="true"
              aria-invalid={!!errors.body}
              aria-describedby={errors.body ? 'email-body-error' : undefined}
            />
            {errors.body && (
              <p id="email-body-error" className="text-sm text-destructive mt-1" role="alert">
                {errors.body}
              </p>
            )}
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-4 border-t">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={sendMutation.isPending}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={sendMutation.isPending}>
              <Send className="mr-2 h-4 w-4" aria-hidden="true" />
              {sendMutation.isPending ? 'Sending...' : 'Send Email'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  )
}
